<template>
    <div id="dropzone-container">
        <!-- 파일 업로드 Dropzone -->
        <form v-show="files.length === 0" id="my-dropzone" class="dropzone" ref="dropzone">
            <div class="dz-message">
                <h3 class="main-title">{{ title }}</h3>
                <p>{{ description }}</p>
                <md-icon class="md-simple md-primary">{{ icon }}</md-icon>
            </div>
        </form>

        <!-- 업로드된 파일 목록 -->
        <div class="file-list">
            <ul>
                <li v-for="(file, index) in files" :key="file.name">
                    <div class="file-list-item">
                        <span class="file-name">{{ file.name }}</span>
                        <div class="file-actions">
                            <button class="btn-delete" @click="removeFile(index)">삭제</button>
                        </div>
                    </div>
                </li>
            </ul>
        </div>

        <!-- 업로드 버튼 -->
        <md-button v-if="!savedFilePath" @click="startUpload" :disabled="isUploadDisabled"
            class="right-button upload-button md-primary md-round">
            {{ uploadButtonLabel }}
        </md-button>
    </div>
</template>
<script>
    import Dropzone from "dropzone";
    import { fileApi } from '@/api/api';

    const server_url = process.env.VUE_APP_SERVER_URI;

    export default {
        name: "UploadFile",
        props: {
            initialFilePath: {
                type: String,
                default: null, // 상위에서 전달된 초기 파일 경로
            },
            clearQueue: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        data() {
            return {
                dropzone: null, // Dropzone 인스턴스
                files: [], // 추가된 파일 목록
                uploading: false, // 업로드 상태,
                savedFilePath: null,
                title: "드래그 앤 드롭으로 파일 업로드 하기",   
                description: "또는 클릭하여 컴퓨터에서 파일을 찾아 업로드 하세요.",
                icon: "cloud_upload",
            };
        },
        computed: {
            // 업로드 버튼 비활성화 상태
            isUploadDisabled() {
                return this.uploading || this.files.length === 0;
            },
            // 업로드 버튼 텍스트
            uploadButtonLabel() {
                return this.uploading ? "Uploading..." : "파일 저장하기";
            },
        },
        methods: {
            // 파일 업로드 시작
            startUpload() {
                if (this.files.length === 0) {
                    alert("업로드할 파일이 없습니다!");
                    return;
                }
                this.uploading = true;
                this.dropzone.processQueue(); // Dropzone 큐 처리
            },

            // Dropzone 초기화
            initializeDropzone() {
                const token = this.$store.state.auth.token; // Vuex에서 토큰 가져오기
                const tokenType = this.$store.state.auth.tokenType || "Bearer";

                this.dropzone = new Dropzone(this.$refs.dropzone, {
                    url: `${server_url}/iskal/api/file/upload`, // 서버 업로드 URL
                    autoProcessQueue: false, // 수동 업로드 설정
                    maxFilesize: 20, // MB
                    maxFiles: 1,
                    acceptedFiles: ".jpeg,.jpg,.png,.gif,.pdf", // 허용할 파일 형식
                    headers: {
                        Authorization: `${tokenType} ${token}` // 인증 헤더 추가
                    },
                    //  addRemoveLinks: true,
                    //  dictRemoveFile: "삭제",
                    dictCancelUpload: "취소",
                    dictCancelUploadConfirmation: "파일 업로드를 취소하시겠습니까?",
                    previewTemplate: "<div></div>"
                });

                // Dropzone 이벤트 등록
                this.dropzone.on("addedfile", this.addFile);
                this.dropzone.on("removedfile", this.removeFileFromDropzone);
                this.dropzone.on("success", this.onUploadSuccess);
                this.dropzone.on("queuecomplete", this.onQueueComplete);
                this.dropzone.on("error", this.onUploadError);

                this.dropzone.enable();

                if (this.initialFilePath) {
                    this.addVirtualFile(this.initialFilePath);
                }
            },
            addVirtualFile(filePath) {
                const mockFile = { name: filePath.split('/').pop(), size: 0 }; // 가상 파일 정보
                this.dropzone.emit("addedfile", mockFile);
                this.files = [mockFile];
                this.savedFilePath = filePath; // 저장된 파일 경로 업데이트
            },
            // 파일 추가 처리
            addFile(file) {
                this.files = [file];
            },

            // Dropzone에서 파일 제거 처리
            removeFileFromDropzone(file) {
                this.files = this.files.filter((f) => f !== file);
            },

            // 파일 목록에서 제거
            removeFile(index) {
                const file = this.files[index];
                if (this.savedFilePath) {
                    this.deleteFile(this.savedFilePath).then(() => {
                        this.dropzone.removeFile(file); // Dropzone에서 파일 제거
                        this.dropzone.removeAllFiles(true); // Dropzone 상태 초기화
                        this.files = []; // 로컬 파일 목록 초기화
                        this.dropzone.enable();
                        this.$emit("cleared"); // 상위 컴포넌트에 이벤트 전달
                    }).catch((error) => {
                        alert("파일 삭제에 실패했습니다.");
                    });
                } else {
                    this.dropzone.removeFile(file); // Dropzone에서 파일 제거
                    this.files = []; // 로컬 파일 목록 초기화
                    this.dropzone.enable();
                    this.$emit("cleared"); // 상위 컴포넌트에 이벤트 전달
                }
            },

            // 업로드 성공 핸들러
            onUploadSuccess(file, response) {
                this.savedFilePath = response.path;
                this.$emit("update-saved-file-path", this.savedFilePath);
            },

            // 큐 처리 완료 핸들러
            onQueueComplete() {
                this.uploading = false;
            },

            // 업로드 오류 핸들러
            onUploadError(file, errorMessage) {
                alert("파일 저장에 실패했습니다. ")
            },
            // 서버에 파일 삭제 요청
            async deleteFile(filename) {
                try {
                    const params = { file_name: filename };
                    const response = await fileApi.deleteFile(params);

                    if (response.status !== 200) {
                        throw new Error("Server failed to delete the file");
                    }

                    this.savedFilePath = null; // 저장된 파일 경로 초기화
                    this.$emit("update-saved-file-path", this.savedFilePath);
                } catch (error) {
                    throw error;
                }
            },
        },
        watch: {
            clearQueue(newValue) {
                if (newValue) {
                    if (newValue) {
                        this.dropzone.removeAllFiles(true);
                        this.files = [];
                        this.dropzone.enable();
                        this.$emit("cleared"); // 상위 컴포넌트에 이벤트 전달
                    }
                }
            },
        },
        mounted() {
            this.initializeDropzone();
        },
    };
</script>

<style lang="scss" scoped>
    $primary-color: #ff9800;
    $primary-shadow-color: #fdb242;
    $description-font-color: #616161;

    .dropzone {
        border: 2px dashed #ddd;
        padding: 20px;
        border-radius: 5px;
        width: 100%;
        display: flex;
        justify-content: center;
    }

    .dz-message {
        font-size: 16px;
        color: #888;
        text-align: center;
    }

    .upload-button {
        background-color: #007bff;
        color: white;
        border: none;
        cursor: pointer;
    }

    .upload-button:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }

    .main-title {
        color: $description-font-color;
        font-weight: 700;
    }

    .right-button {
        display: flex;
        justify-self: flex-end;
    }

    .dropzone .dz-preview .dz-progress {
        height: 0 !important;
    }

    .dz-preview {
        display: none !important;
    }

    .file-list-item {
        display: flex;
        flex-direction: row;
    }

    .file-name {
        padding-right: 10px;
    }
</style>